import { Controller } from "@hotwired/stimulus"

/**
 * Custom Select Dropdown Controller
 * 
 * A beautiful, searchable dropdown component with icons, labels, and counts.
 * 
 * Usage:
 * <div data-controller="custom-select" data-custom-select-current-value="all">
 *   <button class="dropdown-toggle" data-action="click->custom-select#toggle" data-custom-select-target="toggle">
 *     <span class="dropdown-icon" data-custom-select-target="selectedIcon">
 *       <i class="bi bi-funnel"></i>
 *     </span>
 *     <span class="dropdown-text">
 *       <span class="dropdown-label" data-custom-select-target="selectedLabel">All Types</span>
 *       <span class="dropdown-count" data-custom-select-target="selectedCount">(450)</span>
 *     </span>
 *     <i class="bi bi-chevron-down dropdown-arrow"></i>
 *   </button>
 *   
 *   <div class="dropdown-menu" data-custom-select-target="menu">
 *     <div class="dropdown-search">
 *       <i class="bi bi-search"></i>
 *       <input type="text" 
 *              placeholder="Search..."
 *              data-action="input->custom-select#search"
 *              data-custom-select-target="searchInput"
 *              class="dropdown-search-input">
 *     </div>
 *     
 *     <div class="dropdown-items" data-custom-select-target="itemsContainer">
 *       <button class="dropdown-item active" 
 *               data-action="click->custom-select#select"
 *               data-value="all"
 *               data-label="All Types"
 *               data-count="450">
 *         <span class="item-icon"><i class="bi bi-grid-3x3-gap"></i></span>
 *         <span class="item-label">All Types</span>
 *         <span class="item-count">450</span>
 *       </button>
 *       <!-- More items... -->
 *     </div>
 *   </div>
 * </div>
 * 
 * Events dispatched:
 * - custom-select:change - When selection changes, with detail: { value, label, count }
 */
export default class extends Controller {
  static targets = [
    "toggle", "menu", "searchInput", "itemsContainer",
    "selectedIcon", "selectedLabel", "selectedCount"
  ]
  
  static values = {
    current: String,
    searchEnabled: { type: Boolean, default: true }
  }

  connect() {
    this.isOpen = false
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  handleClickOutside(event) {
    if (this.element && !this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.stopPropagation()
    this.isOpen = !this.isOpen
    
    if (this.isOpen) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.isOpen = true
    this.menuTarget.classList.add('open')
    this.toggleTarget.classList.add('active')
    
    // Focus search input if enabled
    if (this.searchEnabledValue && this.hasSearchInputTarget) {
      setTimeout(() => {
        this.searchInputTarget.focus()
      }, 100)
    }
  }

  close() {
    this.isOpen = false
    
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove('open')
    }
    
    if (this.hasToggleTarget) {
      this.toggleTarget.classList.remove('active')
    }
    
    // Clear search
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
      this.search({ target: { value: '' } })
    }
  }

  search(event) {
    const query = event.target.value.toLowerCase()
    const items = this.itemsContainerTarget.querySelectorAll('.dropdown-item')
    
    items.forEach(item => {
      const label = item.querySelector('.item-label').textContent.toLowerCase()
      const value = item.dataset.value
      
      // Always show "All" or first item
      if (value === this.currentValue || label.includes(query)) {
        item.style.display = ''
      } else {
        item.style.display = 'none'
      }
    })
  }

  select(event) {
    const button = event.currentTarget
    const value = button.dataset.value
    const label = button.querySelector('.item-label').textContent
    const count = button.dataset.count
    const icon = button.querySelector('.item-icon').innerHTML
    
    // Update current value
    this.currentValue = value
    
    // Update toggle button display
    if (this.hasSelectedIconTarget) {
      this.selectedIconTarget.innerHTML = icon
    }
    
    if (this.hasSelectedLabelTarget) {
      this.selectedLabelTarget.textContent = label
    }
    
    if (this.hasSelectedCountTarget && count) {
      this.selectedCountTarget.textContent = `(${count})`
    }
    
    // Update active state
    this.itemsContainerTarget.querySelectorAll('.dropdown-item').forEach(item => {
      item.classList.remove('active')
    })
    button.classList.add('active')
    
    // Close dropdown
    this.close()
    
    // Dispatch custom event for parent controllers to listen
    this.dispatch('change', {
      detail: { value, label, count },
      bubbles: true
    })
  }
}
